import {
  formatDollars,
  formatPercent,
  offerTermsSummary,
} from "@sill/shared/components/marketplace/offer-card";

import type { OfferForBuyer, OffersInbox } from "./marketplace-offers-api";

/**
 * The hero's "Best offer so far" highlight (live_offers only). Picks the
 * buyer's strongest unlocked offer across BOTH inbox lanes (active offers +
 * each standing match's primary) by rebate % first, then cash-lane total —
 * the same two figures `offerTermsSummary` (the shared OfferCard source of
 * truth) reports, so the hero line can never disagree with the card it
 * links to. An offer carrying neither figure (a bare-commission standing
 * offer) is not a candidate; no candidate → null and the hero keeps its
 * generic sub-line.
 */
export interface HomeBestOffer {
  /** "1% rebate + $5,000 in cash incentives" — rebate and/or cash only. */
  line: string;
  proDisplayName?: string;
  href: string;
}

export function pickHomeBestOffer(
  inbox: Pick<OffersInbox, "made_for_you" | "available_to_you"> | null | undefined,
): HomeBestOffer | null {
  if (!inbox) return null;
  const candidates: OfferForBuyer[] = [
    ...inbox.made_for_you,
    ...inbox.available_to_you.map((match) => match.primary),
  ].filter((offer) => !offer.locked);

  let best: { offer: OfferForBuyer; rebate: number; cash: number } | null = null;
  for (const offer of candidates) {
    const { rebatePct, cashTotal } = offerTermsSummary(offer.items);
    if (rebatePct == null && cashTotal <= 0) continue;
    const rebate = rebatePct ?? -1;
    if (
      !best ||
      rebate > best.rebate ||
      (rebate === best.rebate && cashTotal > best.cash)
    ) {
      best = { offer, rebate, cash: cashTotal };
    }
  }
  if (!best) return null;

  return {
    line: bestOfferLine(best.offer),
    proDisplayName: best.offer.pro_display_name,
    href: bestOfferHref(best.offer),
  };
}

function bestOfferLine(offer: OfferForBuyer): string {
  const { rebatePct, cashTotal } = offerTermsSummary(offer.items);
  const parts: string[] = [];
  if (rebatePct != null) parts.push(`${formatPercent(rebatePct)} rebate`);
  if (cashTotal > 0) parts.push(`${formatDollars(cashTotal)} in cash incentives`);
  return parts.join(" + ");
}

// Same route shapes `OffersTeaser` links to — never a new destination.
function bestOfferHref(offer: OfferForBuyer): string {
  if (offer.kind === "active") {
    return offer.offer_id ? `/offers/${offer.offer_id}` : "/offers";
  }
  return `/market/agents/${offer.pro_user_id}/offers/${offer.buyer_tier}`;
}
